import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/helper/api";

export interface TemplateSyntaxError {
  code: "syntax_error" | "missing_template";
  message: string;
  line: number | string | null;
  template: string;
}

interface ValidationResult {
  text: string;
  name: string;
  status: "checking" | "valid" | "invalid" | "unavailable";
  errors: TemplateSyntaxError[];
}

export function getTemplateSyntaxErrors(error: unknown): TemplateSyntaxError[] {
  if (!error || typeof error !== "object" || !("text" in error)) return [];
  if (!Array.isArray(error.text)) return [];
  return error.text.filter(
    (issue): issue is TemplateSyntaxError =>
      issue !== null &&
      typeof issue === "object" &&
      (issue.code === "syntax_error" || issue.code === "missing_template") &&
      typeof issue.message === "string"
  );
}

export function useTemplateSyntaxValidation(
  text: string,
  name: string,
  enabled: boolean
) {
  const [result, setResult] = useState<ValidationResult | null>(null);
  const activeRequest = useRef<AbortController | null>(null);
  const currentInput = useRef({ text, name, enabled });
  currentInput.current = { text, name, enabled };

  useEffect(() => {
    if (!enabled) {
      setResult(null);
      return;
    }
    const controller = new AbortController();
    activeRequest.current = controller;
    setResult({ text, name, status: "checking", errors: [] });
    const timer = window.setTimeout(() => {
      if (controller.signal.aborted) return;
      api.genericApiRequest({
        entity: "documentTemplate",
        method: "post",
        additionalRouteParts: { validate_syntax: "/" },
        submitData: { text, name },
        disableGlobalSpinner: true,
        config: { signal: controller.signal },
        successHandler: (response: {
          data: { valid: boolean; errors: TemplateSyntaxError[] };
        }) => {
          if (controller.signal.aborted) return;
          setResult({
            text,
            name,
            status: response.data.valid ? "valid" : "invalid",
            errors: response.data.errors,
          });
        },
        failHandler: () => {
          if (controller.signal.aborted) return;
          setResult({ text, name, status: "unavailable", errors: [] });
        },
      });
    }, 700);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [text, name, enabled]);

  const reportSaveValidation = useCallback(
    (
      errors: TemplateSyntaxError[],
      submittedText: string,
      submittedName: string
    ) => {
      if (
        !currentInput.current.enabled ||
        currentInput.current.text !== submittedText ||
        currentInput.current.name !== submittedName
      )
        return;
      activeRequest.current?.abort();
      setResult({
        text: submittedText,
        name: submittedName,
        status: errors.length ? "invalid" : "valid",
        errors,
      });
    },
    []
  );

  const validation =
    enabled && result?.text === text && result.name === name ? result : null;
  return { validation, reportSaveValidation };
}
